// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * What never goes into a bundle.
 *
 * THIS LIST IS HARDCODED, AND ITS INDEPENDENCE FROM `.gitignore` IS A SECURITY
 * PROPERTY RATHER THAN AN IMPLEMENTATION DETAIL.
 *
 * The best attack on this tool is two steps and entirely non-obvious: a crafted
 * reply rewrites your `.gitignore` to add `!.env` and `!*.pem`; you accept it,
 * because a change to a `.gitignore` looks harmless; you re-bundle; your secrets
 * are now in `bundle.md`; and you paste it into a chat window yourself. Nothing
 * in that chain is an exploit — it is the tool working as designed, with its
 * policy sourced from a file the attacker just wrote.
 *
 * So: the refusal list cannot be re-included by a `!` negation, cannot be
 * switched off in the interface, and `.gitignore` is itself a protected write
 * target. Policy is compiled in.
 *
 * MATCH ON EXTENSIONS AND EXACT BASENAMES. NEVER SUBSTRINGS. A `*key*` rule —
 * the obvious first attempt — silently drops `keyboard.ts`, `apiKey.ts`,
 * `keygen.rs`, `hotkey.css`, `monkey.js` and `turkey.py` out of the bundle, and
 * a bundle that is quietly missing files is worse than no bundle: the model
 * answers confidently about code it was never shown.
 */

export interface SecretVerdict {
  readonly refused: boolean;
  /** One sentence naming the file and why, shown in the excluded list. */
  readonly reason: string | null;
}

/**
 * `.env.example` and friends are the OPPOSITE of a secret — they exist to be
 * committed and read.
 *
 * Both forms are needed. A rule that only carves out the two-segment
 * `.env.example` refuses `.env.local.example`, `.env.development.example`,
 * `.env.production.sample` and `.env.test.template`, all of which Next.js, Vite
 * and Laravel projects genuinely ship.
 */
const ENV_TEMPLATE = /^\.env\.(?:[^.]+\.)?(?:example|sample|template|dist|defaults?|schema)$/i;
const ENV_TYPES = /^\.env.*\.d\.ts$/i;
const ENV_ANY = /^\.env(?:$|\.)/i;

const CREDENTIAL_BASENAMES = new Set([
  '.envrc', '.netrc', '_netrc', '.npmrc', '.pypirc', '.git-credentials', '.htpasswd',
  '.pgpass', '.dockercfg', 'kubeconfig', 'credentials', 'service-account.json',
  'terraform.tfstate', 'terraform.tfstate.backup', 'secrets.json', 'secrets.yml',
  'secrets.yaml', 'secrets.toml', 'id_rsa', 'id_dsa', 'id_ecdsa', 'id_ed25519',
]);

const CLIENT_SECRET = /^client_secret.*\.json$/i;
const TFVARS = /\.tfvars$/i;
const TFVARS_TEMPLATE = /\.(?:example|sample|template)\.tfvars$|\.tfvars\.(?:example|sample|template)$/i;

/** Extensions that are a private key often enough to refuse by default. */
const KEY_EXTS = new Set(['key', 'p12', 'pfx', 'jks', 'keystore', 'ppk', 'gpg']);

/**
 * `.pem` and `.asc` are the two that need looking inside.
 *
 * In the Python and Java worlds they are overwhelmingly PUBLIC material —
 * `cacert.pem` and other CA bundles, `KEYS.asc`, `RELEASE.asc`. Refusing them on
 * the extension alone drops files from the bundle that were never secret, and
 * the user never finds out why the model could not see them. So these two are
 * decided on content.
 */
const AMBIGUOUS_EXTS = new Set(['pem', 'asc', 'crt', 'cer', 'der']);
const PRIVATE_KEY_BODY = /-----BEGIN (?:[A-Z0-9 ]*)PRIVATE KEY-----|-----BEGIN PGP PRIVATE KEY BLOCK-----/;

/** Directories that are never source, at any depth. */
const REFUSED_DIRS = new Set(['.git', '.hg', '.svn', '.ssh', '.gnupg', '.aws', '.azure', '.terraform', '.direnv']);

/**
 * Build output, refused UNCONDITIONALLY — even when the user turns ignore rules
 * off.
 *
 * Vite's own documentation: `VITE_*` variables "should not contain sensitive
 * information such as API keys" because the values "are bundled into your source
 * code at build time" and "statically replaced at build time". So refusing
 * `.env` while including `dist/` ships exactly the same values, as string
 * literals in minified JavaScript, and the refusal that the user can see was
 * cosmetic.
 */
const BUILD_DIRS = new Set(['dist', 'build', 'out', '.next', '.nuxt', '.output', '.svelte-kit', 'coverage', '.turbo', '.parcel-cache']);

export function extensionOf(basename: string): string {
  const dot = basename.lastIndexOf('.');
  return dot > 0 ? basename.slice(dot + 1).toLowerCase() : '';
}

/**
 * Decide one file. `head` is the first few KB of its text where available —
 * only the two ambiguous extensions consult it.
 */
export function checkSecret(relPath: string, head?: string): SecretVerdict {
  const segments = relPath.split('/');
  const basename = segments[segments.length - 1] ?? '';
  const lower = basename.toLowerCase();
  const dirs = segments.slice(0, -1).map((d) => d.toLowerCase());
  const ext = extensionOf(lower);

  for (const dir of dirs) {
    if (REFUSED_DIRS.has(dir)) {
      return { refused: true, reason: `inside ${dir}/ — repository internals or credentials` };
    }
    if (BUILD_DIRS.has(dir)) {
      return {
        refused: true,
        reason: `inside ${dir}/ — build output, which can contain your .env values as plain strings`,
      };
    }
  }

  if (ENV_ANY.test(lower) && !ENV_TEMPLATE.test(lower) && !ENV_TYPES.test(lower)) {
    return { refused: true, reason: 'an environment file — this is where secrets live' };
  }
  if (CREDENTIAL_BASENAMES.has(lower) || CLIENT_SECRET.test(lower)) {
    return { refused: true, reason: 'a credential file' };
  }
  if (TFVARS.test(lower) && !TFVARS_TEMPLATE.test(lower)) {
    return { refused: true, reason: 'Terraform variables, which routinely hold credentials' };
  }
  if (KEY_EXTS.has(ext)) {
    return { refused: true, reason: `a .${ext} key file` };
  }
  if (AMBIGUOUS_EXTS.has(ext)) {
    // Decided on content: a CA bundle and a private key share an extension.
    if (head && PRIVATE_KEY_BODY.test(head)) {
      return { refused: true, reason: `a .${ext} file containing a PRIVATE KEY block` };
    }
    return { refused: false, reason: null };
  }

  return { refused: false, reason: null };
}

/**
 * Look for a refused file's own values inside the files that ARE going in the
 * bundle.
 *
 * BEST EFFORT, AND SAID SO EVERYWHERE IT IS SHOWN. A build tool replaces the
 * value and THEN minifies, so the same secret can arrive split across a template
 * literal, re-escaped inside JSON, or base64'd — none of which a literal search
 * finds. This warns; it never certifies.
 *
 * Values shorter than eight characters, and ones that are plainly not secret
 * (`true`, `3000`, `production`), are skipped: searching a bundle for `1` or
 * `en` produces tens of thousands of hits and teaches the user to ignore the
 * warning entirely.
 */
export function parseEnvValues(text: string): string[] {
  const values: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const m = /^\s*(?:export\s+)?[A-Za-z_][A-Za-z0-9_]*\s*=\s*(.*)$/.exec(line);
    if (!m) continue;
    let value = m[1].trim();
    if (/^".*"$/.test(value) || /^'.*'$/.test(value)) value = value.slice(1, -1);
    value = value.replace(/\s+#.*$/, '').trim();
    if (value.length < 8) continue;
    if (/^(?:true|false|null|undefined|production|development|localhost)$/i.test(value)) continue;
    if (/^\d+$/.test(value)) continue;
    values.push(value);
  }
  return [...new Set(values)];
}

export interface LeakHit {
  readonly path: string;
  /** The value is NEVER echoed back — only how much of it, and where. */
  readonly length: number;
  readonly variable: string | null;
}

export function findLeaks(
  values: readonly { name: string | null; value: string }[],
  files: readonly { path: string; text: string | null }[],
): LeakHit[] {
  const hits: LeakHit[] = [];
  for (const file of files) {
    if (!file.text) continue;
    for (const v of values) {
      if (!file.text.includes(v.value)) continue;
      hits.push({ path: file.path, length: v.value.length, variable: v.name });
    }
  }
  return hits;
}
